import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GitMerge, Loader2, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader } from '@/components/ui/page-header';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import {
  useMergeTiers,
  useTierDoublons,
  useTiers,
  type TierDoublonGroup,
} from '@/hooks/queries/useTiers';
import { useRole } from '@/hooks/useRole';
import { formatNumberDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { TierRow } from '@/lib/database.types';

export function DoublonsTiersPage(): JSX.Element {
  const { isAdmin, role } = useRole();
  const canMerge = isAdmin || role === 'direction' || role === 'responsable_activite';
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');
  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const { data: groups = [], isLoading } = useTierDoublons(entrepriseId);
  const [activeGroup, setActiveGroup] = useState<TierDoublonGroup | null>(null);

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Doublons potentiels — Tiers"
        subtitle="Détection automatique par normalisation (lowercase, accents, formes juridiques retirées). Fusionne pour réattribuer toutes les opérations au tiers maître."
        actions={
          <select
            value={entrepriseId}
            onChange={(e) => setEntrepriseId(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {entreprises.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nom}
              </option>
            ))}
          </select>
        }
      />

      {!canMerge && (
        <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-xs text-warning">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span>
            Seuls les rôles super_admin, direction ou responsable d&apos;activité peuvent fusionner.
            Vous voyez la liste en lecture seule.
          </span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Analyse en cours…
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Aucun doublon détecté pour cette entreprise. 🎉
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            <strong>{groups.length}</strong> groupe(s) de doublons détecté(s) ·{' '}
            <strong>{formatNumberDZ(groups.reduce((acc, g) => acc + g.nb_doublons, 0))}</strong>{' '}
            tier(s) concerné(s)
          </div>
          {groups.map((g) => (
            <article key={g.nom_normalise} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">
                    Forme normalisée :{' '}
                    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                      {g.nom_normalise}
                    </code>
                  </div>
                  <ul className="mt-2 space-y-0.5 pl-1 text-xs">
                    {g.raisons_sociales.map((rs, i) => (
                      <li key={i} className="text-muted-foreground">
                        • {rs}{' '}
                        <span className="font-mono text-[10px]">
                          ({g.tier_ids[i]?.slice(0, 8)}…)
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge variant="warning">{g.nb_doublons} doublons</Badge>
                  {canMerge && (
                    <Button size="sm" onClick={() => setActiveGroup(g)}>
                      <GitMerge className="h-4 w-4" /> Fusionner
                    </Button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {activeGroup && (
        <MergeDialog
          open={!!activeGroup}
          onOpenChange={(o) => !o && setActiveGroup(null)}
          group={activeGroup}
          onMerged={() => setActiveGroup(null)}
        />
      )}
    </div>
  );
}

function MergeDialog({
  open,
  onOpenChange,
  group,
  onMerged,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  group: TierDoublonGroup;
  onMerged: () => void;
}): JSX.Element {
  const merge = useMergeTiers();
  const { data: tiers = [] } = useTiers({ entrepriseId: group.entreprise_id, search: '' });
  const groupTiers: TierRow[] = group.tier_ids
    .map((id) => tiers.find((t) => t.id === id))
    .filter((t): t is TierRow => !!t);

  const [masterId, setMasterId] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open && groupTiers[0]) setMasterId(groupTiers[0].id);
  }, [open, groupTiers]);

  const slaveIds = group.tier_ids.filter((id) => id !== masterId);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Fusionner {group.nb_doublons} tiers</DialogTitle>
            <DialogDescription>
              Choisissez le tiers maître. Les autres seront supprimés et leurs opérations
              réattribuées.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-3">
            {groupTiers.map((t) => {
              const isMaster = t.id === masterId;
              return (
                <label
                  key={t.id}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
                    isMaster ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40',
                  )}
                >
                  <input
                    type="radio"
                    checked={isMaster}
                    onChange={() => setMasterId(t.id)}
                    className="mt-1 h-4 w-4 accent-primary"
                  />
                  <div className="flex-1 space-y-0.5 text-sm">
                    <div className="font-semibold">
                      {t.raison_sociale}
                      {isMaster && <Badge className="ml-2">Maître (gardé)</Badge>}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <span>Code : {t.code ?? '—'}</span>
                      <span>NIF : {t.nif ?? '—'}</span>
                      <span>Email : {t.email ?? '—'}</span>
                      <span>Tél. : {t.telephone ?? '—'}</span>
                      <span>Ville : {t.ville ?? '—'}</span>
                      <span>Type : {t.type}</span>
                    </div>
                  </div>
                </label>
              );
            })}
            <div className="rounded-md border border-warning/30 bg-warning/10 p-3 text-xs">
              <strong>Aperçu :</strong> {slaveIds.length} tier(s) seront supprimés. Toutes leurs
              opérations seront réattribuées au tiers maître. Action journalisée dans audit_log.
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={slaveIds.length === 0}
              onClick={() => setConfirmOpen(true)}
            >
              <GitMerge className="h-4 w-4" /> Fusionner ({slaveIds.length} → 1)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DangerousConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Fusionner ${slaveIds.length + 1} tiers en un seul ?`}
        description="Action irréversible (mais loggée dans audit_log). Une sauvegarde manuelle préalable est recommandée."
        consequences={[
          `${slaveIds.length} tier(s) seront supprimés`,
          `Toutes les opérations associées seront réattribuées au tiers maître`,
          `Les références futures (factures, échéances Sprint 3+) suivront le maître`,
          `1 entrée audit_log "MERGE" sera créée`,
        ]}
        challenge="FUSIONNER"
        loading={merge.isPending}
        confirmLabel="Lancer la fusion"
        onConfirm={async () => {
          try {
            const r = await merge.mutateAsync({ master_id: masterId, slave_ids: slaveIds });
            toast.success(
              `Fusion OK : ${r.nb_slaves_supprimes} tier(s) supprimé(s) · ${r.nb_operations_reaffectees} op(s) réattribuée(s)`,
            );
            setConfirmOpen(false);
            onMerged();
          } catch (e) {
            toast.error('Erreur : ' + (e as Error).message);
          }
        }}
      />
    </>
  );
}
